import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getRemoteStartTransactionDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/ocpp/remote-start-transaction.form-default-values';
import { RemoteStartTransactionModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/ocpp/remote-start-transaction.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoStartTransactionOcppMutation } from '@app/core/service/cpms-bo-charge-point-ocpp.service';

import {
  StartChargePointChannelTransactionContextProvider,
  useChargePointChannelContext,
  useChargePointContext,
} from '../charge-point.context';
import { ChargePointChannelTransactionStartFormController } from './charge-point-channel-transaction-start-form/charge-point-channel-transaction-start-form.controller';

interface ChargePointChannelTransactionStartControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const ChargePointChannelTransactionStartController = ({
  onCancel,
  onSuccess,
}: ChargePointChannelTransactionStartControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [startTransactionOcppMutation] =
    useCpmsBoStartTransactionOcppMutation();
  const chargePoint = useChargePointContext();
  const channel = useChargePointChannelContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<
    Pick<
      RemoteStartTransactionModel,
      'chargePointId' | 'channelId' | 'tokenIdentifier'
    >
  > = (errors) => {
    Logger.error(errors);
    toast.error(
      t('$.charge-point-channel-transaction-start.error', {
        error: t('global.unknown'),
      }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<
      RemoteStartTransactionModel,
      'chargePointId' | 'channelId' | 'tokenIdentifier'
    >
  > = (fieldValues) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    startTransactionOcppMutation({
      chargePointId: fieldValues.chargePointId,
      channelId: fieldValues.channelId,
      tokenIdentifier: fieldValues.tokenIdentifier,
    })
      .unwrap()
      .then(() => {
        toast.success(t('$.charge-point-channel-transaction-start.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.charge-point-channel-transaction-start.error', {
            error: errorMessage,
          }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getRemoteStartTransactionDefaultValues(
    chargePoint.id,
    channel.id,
  );

  return (
    <StartChargePointChannelTransactionContextProvider data={data}>
      <ChargePointChannelTransactionStartFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </StartChargePointChannelTransactionContextProvider>
  );
};
